/* eslint-disable no-underscore-dangle */
import { AxiosInstance } from 'axios'
import QuotaDTO, { mapFromApiQuotasResponse } from '@/contexts/quota-dashboard/infrastructure/quotaDTO'
import { mapOfferFromOfferApi, OfferDTO } from '@/contexts/quota-dashboard/infrastructure/offerDTO'
import { CooperativeProduct } from '@/core/product'
import { ProductSDK, ProduitCatalogueCooperativeApiResponse } from '@b2ag/module-product-sdk'
import { mapProductFromApiAndFlattenPackProduct } from '@/core/mapper/product.mapper'
import { OfferSchema, QuotaSchema } from '@b2ag/types-api-schemas'
import { splitRequest } from '@invivodf/common/src/infrastructure/splitRequest'
import { createAxiosClientWithAladinBearer } from '@/utils/axios'

export default class QuotaDashboardDatasource {
  private readonly quotaAPI: AxiosInstance

  private readonly offerAPI: AxiosInstance

  private readonly productSdk: ProductSDK

  constructor({ quotaApiHost, offerApiHost, productSdk }) {
    this.quotaAPI = createAxiosClientWithAladinBearer(`${quotaApiHost}/v1`, { timeout: 20000 })
    this.offerAPI = createAxiosClientWithAladinBearer(`${offerApiHost}/v3`, { timeout: 20000 })
    this.productSdk = productSdk
  }

  async findQuotas(distributorId: number): Promise<QuotaDTO[]> {
    const { data } = await this.quotaAPI.get<QuotaSchema[]>('/quotas', {
      params: {
        partner_id: distributorId,
      },
    })
    return mapFromApiQuotasResponse(data)
  }

  private async _findOffers(distributorId: number, offerIds: readonly string[]): Promise<OfferDTO[]> {
    const { data } = await this.offerAPI.get<OfferSchema[]>('/offers', {
      params: {
        pagination: false,
        partner_id: distributorId,
        ids: offerIds.join(','),
      },
    })
    return data.map(mapOfferFromOfferApi)
  }

  findOffers(offerIds: readonly string[], distributorId: number): Promise<OfferDTO[]> {
    return splitRequest(offerIds, this._findOffers.bind(this, distributorId))
  }

  async _findProducts(distributorId: number, variantInvivoIdList: string[]): Promise<CooperativeProduct[]> {
    const products = (await this.productSdk.getDistributorCatalogProducts(
      { cooperativeId: String(distributorId) },
      { invivo_ids: variantInvivoIdList },
    )) as ProduitCatalogueCooperativeApiResponse[]

    return products.map(mapProductFromApiAndFlattenPackProduct)
  }

  findProducts(variantInvivoIdList: string[], distributorId: number): Promise<CooperativeProduct[]> {
    return splitRequest(variantInvivoIdList, this._findProducts.bind(this, distributorId), { maxLen: 3000 })
  }
}
